import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { LocalClearanceBatch, MonthOption, Exception } from '../../Types';
import { overviewService } from '../../services/overviewServiceInstance';

const createMonthOptions = () => {
  const currentDateForMonth = new Date();
  const previousDateForMonth = new Date()
  previousDateForMonth.setDate(0);

  return [{
    month: currentDateForMonth.getMonth() + 1,
    year: currentDateForMonth.getFullYear(),
  }, {
    month: previousDateForMonth.getMonth() + 1,
    year: previousDateForMonth.getFullYear(),
  }] as MonthOption[];
}

export const findBatchesByMonth = createAsyncThunk('findBatchesByMonth', async (monthOption : MonthOption) => {
  return await overviewService.findBatchJobsByMonth(monthOption);
});

export const createAndProduceBatches = createAsyncThunk('createAndProduceBatches', async (batches : LocalClearanceBatch[]) => {
  return await overviewService.createAndProduceBatches(batches);
});

export const findExceptions = createAsyncThunk('findExceptions', async () => {
  return await overviewService.findExceptions();
});

export const retryExceptions = createAsyncThunk('retryExceptions', async (exceptions : Exception[]) => {
  return await overviewService.retryExceptions(exceptions);
});

export const flushFilingPacks = createAsyncThunk('flushFilingPacks', async (userId: string) => {
  return await overviewService.flushFilingPacks(userId);
});

const monthOptions = createMonthOptions();

export const overviewSlice = createSlice({
  name: 'overview',
  initialState: {
    isLoading: true,
    batches: [] as LocalClearanceBatch[],
    monthOptions: monthOptions,
    activeMonthOption: monthOptions[0],
    isModalVisible: false,
    isFlushFilingPacksModalVisible: false,
    notification: null as any,
    viewMode: 'BATCHES' as 'BATCHES' | 'PROBLEMS',
    exceptions: [] as Exception[],
    isExceptionsLoading: false,
    exceptionsLoaded: false
  },
  reducers: {
    changeMonth: (state, action) => {
      state.activeMonthOption = action.payload;
    },
    toggleBatch: (state, action) => {
      const batch = state.batches.find(batch => {
        return batch.day_bucket_date === action.payload.day_bucket_date;
      }) as LocalClearanceBatch;

      batch.selected = !batch.selected;
    },
    toggleModalConfirmation: (state, action) => {
      state.isModalVisible = action.payload;
    },
    toggleFlushFilingPacksModal: (state, action) => {
      state.isFlushFilingPacksModalVisible = action.payload;
    },
    toggleNotification: (state, action) => {
      state.notification = action.payload;
    },
    toggleExceptionSelection: (state, action) => {
      const exception = state.exceptions.find(exception => exception.id === action.payload.id);
      if (exception) {
        exception.selected = !exception.selected;
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(findBatchesByMonth.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(findBatchesByMonth.fulfilled, (state, action) => {
      state.isLoading = false;
      state.batches = (action.payload.data.findBatchJobsByMonth as LocalClearanceBatch[]).map(batch => ({
        ...batch,
        selected: false
      }));
    })
    .addCase(findBatchesByMonth.rejected, (state, action) => {
      state.isLoading = false;
      state.notification = {
        status: 'error',
        message: action.error.message || 'Failed to load batches'
      };
    }).addCase(createAndProduceBatches.pending, (state) => {
      state.isLoading = true;
    }).addCase(createAndProduceBatches.rejected, (state, action: any) => {
      state.isLoading = false;
      state.isModalVisible = false;
      state.notification = {
        status: 'error',
        message: action.error.message
      };
    })
    .addCase(createAndProduceBatches.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isModalVisible = false;
      state.notification = {
        status: 'success',
        message: 'Batches successfully released for declaration',
      };

      action.payload.data.createAndProduceBatches.forEach((x: LocalClearanceBatch) => {
        const index = state.batches.findIndex((y: LocalClearanceBatch) => y.day_bucket_date === x.day_bucket_date);
        if (index >= 0) {
          state.batches[index] = { ...state.batches[index], ...x, selectable: false, selected: false };
        }
      });
    })
    .addCase(retryExceptions.pending, (state) => {
      state.isExceptionsLoading = true;
    })
    .addCase(retryExceptions.fulfilled, (state, action) => {
      state.isExceptionsLoading = false;
      state.notification = {
        status: 'success',
        message: 'Exceptions retried successfully'
      };
    })
    .addCase(retryExceptions.rejected, (state, action) => {
      state.isExceptionsLoading = false;
      state.notification = {
        status: 'error',
        message: action.error.message
      };
    })
    .addCase(findExceptions.pending, (state) => {
      state.isExceptionsLoading = true;
    })
    .addCase(findExceptions.fulfilled, (state, action) => {
      state.isExceptionsLoading = false;
      state.exceptionsLoaded = true;
      state.exceptions = action.payload;
    })
    .addCase(findExceptions.rejected, (state, action) => {
      state.isExceptionsLoading = false;
      state.notification = {
        status: 'error',
        message: action.error.message
      };
    })
    .addCase(flushFilingPacks.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(flushFilingPacks.fulfilled, (state) => {
      state.isLoading = false;
      state.isFlushFilingPacksModalVisible = false;
      state.notification = {
        status: 'success',
        message: 'Flush Filing Packs message sent successfully',
      };
    })
    .addCase(flushFilingPacks.rejected, (state, action) => {
      state.isLoading = false;
      state.isFlushFilingPacksModalVisible = false;
      state.notification = {
        status: 'error',
        message: action.error.message
      };
    });
  },
});

export const {
  changeMonth,
  toggleBatch,
  toggleModalConfirmation,
  toggleFlushFilingPacksModal,
  toggleNotification,
  toggleExceptionSelection
} = overviewSlice.actions;

export const selectBatches = (state: any) => state.overview.batches;
export const selectNotification = (state: any) => state.overview.notification;
export const selectIsLoading = (state: any) => state.overview.isLoading;
export const selectIsModalConfirmationVisible = (state: any) => state.overview.isModalVisible;
export const selectIsFlushFilingPacksModalVisible = (state: any) => state.overview.isFlushFilingPacksModalVisible;
export const selectMonthOptions = (state: any) => state.overview.monthOptions;
export const selectActiveMonthOption = (state: any) => state.overview.activeMonthOption;

export const selectViewMode = (state: any) => state.overview.viewMode;
export const selectExceptions = (state: any) => state.overview.exceptions;
export const selectIsExceptionsLoading = (state: any) => state.overview.isExceptionsLoading;
export const selectExceptionsLoaded = (state: any) => state.overview.exceptionsLoaded;

export const selectCheckedBatches = createSelector(
  [selectBatches], (batches) => batches.filter((batch: LocalClearanceBatch) => batch.selected)
);
export const selectCheckedExceptions = createSelector(
  [selectExceptions], (exceptions) => exceptions.filter((exception: Exception) => exception.selected)
);

export default overviewSlice.reducer;